import { gql } from '@apollo/client/core'
import { isBrowser } from '@atomixdesign/rem-scaling'
import { Container, Grid } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import jump from 'jump.js'
import dynamic from 'next/dynamic'
import React, { PropsWithChildren, useLayoutEffect, useMemo } from 'react'
import { LazyComponent } from '../../components/lazyComponent/lazyComponent'
import { nonNull } from '../../utils/nonNull'
import { FaqPageQuery } from './__generated__'
import {
  FAQBanner,
  FAQ_CONTACT_CTA_FRAGMENT,
  POPULAR_GUIDE_ARTICLES_FRAGMENT,
  POPULAR_QUESTIONS_FRAGMENT,
  PopularQuestions,
  SECONDARY_BANNER_FRAGMENT,
} from './fragments'
import {
  StyledFAQBannerWrapper,
  StyledFAQContentWrapper,
  StyledPopularQuestionTitle,
  StyledPopularQuestionsWrapper,
} from './styledFaqs'

export type FaqProps = FaqPageQuery & {
  //
}

const TopContent = dynamic(() => import('./fragments/topContent').then(mod => mod.TopContent))
const BelowTheFold = dynamic(() => import('./fragments/belowTheFold').then(mod => mod.BelowTheFold))

export const FAQ: React.FC<PropsWithChildren<FaqProps>> = (props) => {
  const { entry, entries } = props

  if (!entry || entry.__typename !== 'faqLandingPage_faqLandingPage_Entry') {
    return null
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { faq, category } = useMemo(() => {
    let faq: string | undefined
    let category: string | undefined

    if (isBrowser()) {
      const slug = window.location.hash.slice(1)

      if (slug && entries) {
        const match = nonNull(entries).find((e) => e.__typename === 'faqs_default_Entry' && e.slug === slug)

        if (match && match.__typename == 'faqs_default_Entry') {
          faq = match.id ?? undefined
          category = match.faqCategoryType?.[0]?.id ?? undefined
        }
      }
    }

    return { faq, category }
  }, [entries])

  useLayoutEffect(() => {
    setTimeout(() => {
      if (isBrowser() && faq) {
        const element = document.querySelector(window.location.hash)

        if (element) {
          const heading = element.querySelector('[role="button"]') as HTMLElement

          if (heading) {
            heading.click()
          } else {
            jump(element)
          }
        }
      }
    }, 200)
  }, [faq])

  return (
    <>
      {entry.faqBanner && (
        <StyledFAQBannerWrapper container direction={'column'}>
          <FAQBanner
            secondaryPageBanner={nonNull(entry.faqBanner)}
            layout={isMobile ? 'vertical' : 'horizontal'}
          />
        </StyledFAQBannerWrapper>
      )}
      <StyledFAQContentWrapper maxWidth={false}>
        <Container disableGutters={true}>
          <Grid direction={'column'} alignItems={'center'} container>
            {entry.popularQuestions && (
              <StyledPopularQuestionsWrapper container direction={'column'} alignItems={'center'}>
                <StyledPopularQuestionTitle variant="h2" tabIndex={0}>
                  Popular Questions
                </StyledPopularQuestionTitle>
                <PopularQuestions data={nonNull(entry.popularQuestions)}/>
              </StyledPopularQuestionsWrapper>
            )}
            <LazyComponent>
              <TopContent { ...props } category={category} />
            </LazyComponent>
          </Grid>
        </Container>
      </StyledFAQContentWrapper>
      <LazyComponent>
        <BelowTheFold { ...props } />
      </LazyComponent>
    </>
  )
}

export const FAQ_PAGE_QUERY = gql`
  ${SECONDARY_BANNER_FRAGMENT}
  ${POPULAR_GUIDE_ARTICLES_FRAGMENT}
  ${FAQ_CONTACT_CTA_FRAGMENT}
  ${POPULAR_QUESTIONS_FRAGMENT}

  query FaqPageQuery($uid: [String], $uri: [String]) {
    entry(section: "faqLandingPage", uid: $uid, uri: $uri) {
      ... on faqLandingPage_faqLandingPage_Entry {
        title
        faqBanner {
          ...FaqBannerFragment
        }
        popularGuideArticles {
          ...PopularGuideArticlesFragment
        }
        linksCta {
          ...FaqContactCtaFragment
        }
        popularQuestions {
          ...PopularQuestionsFragment
        }
      }
    }
    categories(group: "categoryFaqs", relatedToEntries: {section: "faqs"}) {
      ... on categoryFaqs_Category {
        id
        title
        slug
      }
    }
    entries(section: "faqs") {
      ... on faqs_default_Entry {
        id
        slug
        title
        faqText
        faqCategoryType {
          title
          id
          slug
        }
      }
    }
  }
`


